import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdmissionProfileCard } from '../../components/admission/AdmissionProfileCard';
import {
  completeCustodianUploadBatch,
  confirmCustodianUpload,
  createCustodianUploadPackage,
  createCustodianUploadSession,
  importCustodianUploadInboxFiles,
  listCustodianProgramInboxFiles,
  listCustodianProgramIngestChannels,
  listCustodianUploadPrograms,
  uploadCustodianFileContent,
  uploadCustodianPackageLoanTape,
} from '../../api/custodianApi';
import { InboxFilePicker } from '../../components/upload/InboxFilePicker';
import { UploadChannelTabs } from '../../components/upload/UploadChannelTabs';
import { LOAN_TAPE_ACCEPT } from '../../api/originatorApi';
import {
  DOCUMENT_ACCEPT,
  DOCUMENT_TYPE_ERROR,
  clubDocumentFiles,
  partitionDocumentFiles,
  planDocumentFiles,
} from '../../upload/documentFiles';
import {
  hasUsableAdmissionProfile,
  programmeProfileUnavailableMessage,
} from '../../admission/selectors';

/**
 * Custodian "Upload batch" on behalf of the program's originator.
 *
 * Same two steps as the Originator New Submission (program, then files). After
 * the files (and optional tape) are confirmed, the batch is completed: the
 * backend classifies it, runs the admission checks and - when they pass with no
 * blocking exception - certifies it on the originator's behalf so it reaches
 * the Provider as CERTIFIED. A batch with exceptions stays CLASSIFIED with the
 * originator to cure. Uses the existing custodian workspace identity, so no new
 * role.
 */
const STEPS = [
  { id: 1, label: 'Assign program' },
  { id: 2, label: 'Upload files' },
];

const FILE_STATUS = {
  pending: 'pending',
  uploading: 'uploading',
  confirming: 'confirming',
  complete: 'complete',
  failed: 'failed',
};

const TAPE_STATUS = {
  none: 'none',
  queued: 'queued',
  uploading: 'uploading',
  attached: 'attached',
  failed: 'failed',
};

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#003A70]/30 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';

const inputClass =
  'w-full rounded-xl border border-[#D1D5DB] bg-white px-6 py-3 text-sm text-[#5A5A5A] placeholder:text-[#8D8D8D] focus:border-[#003A70] focus:outline-none focus:ring-2 focus:ring-[#003A70]/15 disabled:bg-white disabled:text-[#8D8D8D]';
const labelClass = 'block text-sm font-medium text-[#212121]';
const backButtonClass = `inline-flex items-center justify-center rounded-lg border border-[#003A70] bg-[#F8F8F8] px-4 py-3 text-sm font-semibold text-[#003A70] ${focusRing}`;
const nextButtonClass = `inline-flex items-center justify-center rounded-lg bg-[#003A70] px-4 py-3 text-sm font-bold text-white ${focusRing}`;

function Stepper({ step }) {
  return (
    <div className="flex items-center justify-center">
      {STEPS.map((s, i) => {
        const active = s.id === step;
        const done = s.id < step;
        return (
          <div key={s.id} className="flex items-center">
            <div className="flex flex-col items-center">
              <span
                className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${
                  active || done
                    ? 'bg-eorig-navy-600 text-white'
                    : 'border border-eorig-border bg-white text-eorig-muted'
                }`}
              >
                {s.id}
              </span>
              <span
                className={`mt-1.5 text-[11px] font-medium ${
                  active ? 'text-eorig-ink' : 'text-eorig-muted'
                }`}
              >
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 ? (
              <span
                className={`mx-3 mb-5 h-0.5 w-24 rounded-full sm:w-40 ${
                  done ? 'bg-eorig-navy-600' : 'bg-eorig-border'
                }`}
                aria-hidden
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function fileStatusLabel(status, progress = null) {
  switch (status) {
    case FILE_STATUS.uploading:
      return Number.isFinite(progress) && progress > 0 ? `Uploading… ${progress}%` : 'Uploading…';
    case FILE_STATUS.confirming:
      return 'Confirming…';
    case FILE_STATUS.complete:
      return 'Complete';
    case FILE_STATUS.failed:
      return 'Failed';
    default:
      return 'Queued';
  }
}

function createQueuedFile(file, index, memberNames = null) {
  return {
    localId: `${file.name}-${file.size}-${index}-${Date.now()}`,
    source: 'device',
    ingestChannel: null,
    file,
    fileName: file.name,
    mimeType: file.type || null,
    sizeBytes: file.size || 0,
    memberNames: Array.isArray(memberNames) && memberNames.length ? memberNames : null,
    status: FILE_STATUS.pending,
    error: '',
    fileId: null,
    uploadUrl: null,
    contentSha256: null,
  };
}

function createQueuedInboxFile({ name, size_bytes: sizeBytes, channel }) {
  return {
    localId: `inbox-${name}-${Date.now()}`,
    source: 'inbox',
    ingestChannel: channel,
    file: null,
    fileName: name,
    mimeType: null,
    sizeBytes: sizeBytes || 0,
    memberNames: null,
    status: FILE_STATUS.pending,
    error: '',
    fileId: null,
    uploadUrl: null,
    contentSha256: null,
  };
}

export function CustodianUploadBatchPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [programs, setPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [programId, setProgramId] = useState('');
  const [programLookup, setProgramLookup] = useState(null);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [queuedFiles, setQueuedFiles] = useState([]);
  const [tapeFile, setTapeFile] = useState(null);
  const [tapeStatus, setTapeStatus] = useState(TAPE_STATUS.none);
  const [tapeInfo, setTapeInfo] = useState(null);
  const [tapeError, setTapeError] = useState('');
  const [packageId, setPackageId] = useState(null);
  const [createdPackage, setCreatedPackage] = useState(null);
  const [completed, setCompleted] = useState(false);
  // Outcome of POST .../complete (classify + admission checks + certify when clean).
  const [completion, setCompletion] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitPhase, setSubmitPhase] = useState('');
  const [packing, setPacking] = useState(false);
  const [uploadChannelTab, setUploadChannelTab] = useState('DEVICE');
  const [ingestChannels, setIngestChannels] = useState([]);
  const [ingestChannelsLoading, setIngestChannelsLoading] = useState(false);
  const filesRef = useRef(queuedFiles);
  const packageIdRef = useRef(packageId);
  filesRef.current = queuedFiles;
  packageIdRef.current = packageId;

  const profileReady = hasUsableAdmissionProfile(programLookup);
  const admissionProfile = programLookup?.admission_profile || null;

  useEffect(() => {
    let cancelled = false;
    async function loadPrograms() {
      setProgramsLoading(true);
      setError('');
      try {
        const response = await listCustodianUploadPrograms();
        if (cancelled) return;
        const items = Array.isArray(response) ? response : response?.items || [];
        setPrograms(items);
        if (items.length === 1) {
          setProgramId(items[0].id);
          setProgramLookup(items[0]);
          if (!hasUsableAdmissionProfile(items[0])) {
            setError(programmeProfileUnavailableMessage(items[0]));
          }
        }
      } catch (requestError) {
        if (cancelled) return;
        setPrograms([]);
        setError(requestError.message || 'Unable to load programs assigned to this custodian.');
      } finally {
        if (!cancelled) setProgramsLoading(false);
      }
    }
    loadPrograms();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (step !== 2 || !programId) return undefined;
    let cancelled = false;
    async function loadChannels() {
      setIngestChannelsLoading(true);
      try {
        const response = await listCustodianProgramIngestChannels(programId.trim());
        if (cancelled) return;
        const channels = Array.isArray(response?.channels) ? response.channels : [];
        setIngestChannels(channels);
        setUploadChannelTab((active) => {
          const current = channels.find((item) => item.channel === active);
          if (current?.enabled) return active;
          const device = channels.find((item) => item.channel === 'DEVICE' && item.enabled);
          return device ? 'DEVICE' : active;
        });
      } catch {
        if (!cancelled) setIngestChannels([]);
      } finally {
        if (!cancelled) setIngestChannelsLoading(false);
      }
    }
    loadChannels();
    return () => {
      cancelled = true;
    };
  }, [step, programId]);

  const listInboxForProgram = useCallback(
    (channel) => listCustodianProgramInboxFiles(programId.trim(), channel),
    [programId]
  );

  function handleAddInboxFiles(rows) {
    if (!rows?.length) return;
    setQueuedFiles((current) => {
      const names = new Set(current.map((file) => file.fileName));
      const added = rows
        .filter((row) => row.name && !names.has(row.name))
        .map((row) => createQueuedInboxFile(row));
      return added.length ? [...current, ...added] : current;
    });
    setError('');
  }

  function handleSelectProgram(nextId) {
    setProgramId(nextId);
    const selected = programs.find((program) => program.id === nextId) || null;
    setProgramLookup(selected);
    if (selected && !hasUsableAdmissionProfile(selected)) {
      setError(programmeProfileUnavailableMessage(selected));
    } else if (error && /ADM-001/.test(error)) {
      setError('');
    }
  }

  function updateFile(localId, patch) {
    const next = filesRef.current.map((item) =>
      item.localId === localId ? { ...item, ...patch } : item
    );
    filesRef.current = next;
    setQueuedFiles(next);
  }

  function appendQueued(files, memberNames = null) {
    const next = [
      ...filesRef.current,
      ...files.map((file, index) =>
        createQueuedFile(
          file,
          filesRef.current.length + index,
          files.length === 1 ? memberNames : null
        )
      ),
    ];
    filesRef.current = next;
    setQueuedFiles(next);
  }

  async function packAndQueue(pdfs) {
    setPacking(true);
    try {
      const clubbed = await clubDocumentFiles(pdfs);
      if (clubbed.error) {
        setError(clubbed.error);
        return;
      }
      appendQueued(clubbed.files, clubbed.memberNames);
    } catch (packError) {
      setError(packError.message || 'Unable to pack PDFs into a batch ZIP.');
    } finally {
      setPacking(false);
    }
  }

  function addFiles(fileList) {
    const { accepted, rejected } = partitionDocumentFiles(fileList);
    if (rejected.length) {
      setError(
        rejected.length === 1
          ? `${DOCUMENT_TYPE_ERROR} Not queued: ${rejected[0]}.`
          : `${DOCUMENT_TYPE_ERROR} Not queued: ${rejected.join(', ')}.`
      );
    } else if (error && error.startsWith(DOCUMENT_TYPE_ERROR)) {
      setError('');
    }
    if (!accepted.length) return;

    const plan = planDocumentFiles(accepted);
    if (plan.action === 'error') {
      setError(plan.error);
      return;
    }
    if (plan.action === 'keep') {
      appendQueued(plan.files);
      return;
    }
    void packAndQueue(plan.files);
  }

  function handleQueueFiles(event) {
    addFiles(event.target.files);
    event.target.value = '';
  }

  function handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    addFiles(event.dataTransfer?.files);
  }

  function handleDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  function handleQueueTape(event) {
    const [file] = Array.from(event.target.files || []);
    event.target.value = '';
    if (!file) return;
    setTapeFile(file);
    setTapeStatus(TAPE_STATUS.queued);
    setTapeInfo(null);
    setTapeError('');
  }

  function handleRemoveQueuedTape() {
    setTapeFile(null);
    setTapeStatus(TAPE_STATUS.none);
    setTapeInfo(null);
    setTapeError('');
  }

  async function uploadQueuedTape(pkgId) {
    if (!tapeFile || tapeStatus === TAPE_STATUS.attached) return true;
    setTapeStatus(TAPE_STATUS.uploading);
    setTapeError('');
    try {
      const attached = await uploadCustodianPackageLoanTape(pkgId, tapeFile);
      setTapeInfo(attached);
      setTapeStatus(TAPE_STATUS.attached);
      return true;
    } catch (requestError) {
      setTapeStatus(TAPE_STATUS.failed);
      setTapeError(requestError.message || 'Loan tape could not be attached.');
      return false;
    }
  }

  async function importQueuedInboxFiles(pkgId) {
    const pending = filesRef.current.filter(
      (file) => file.source === 'inbox' && file.status !== FILE_STATUS.complete
    );
    if (!pending.length) return true;

    const byChannel = {};
    for (const row of pending) {
      const channel = row.ingestChannel || 'SFTP';
      if (!byChannel[channel]) byChannel[channel] = [];
      byChannel[channel].push(row);
    }

    let ok = true;
    for (const [channel, rows] of Object.entries(byChannel)) {
      const response = await importCustodianUploadInboxFiles(pkgId, {
        channel,
        fileNames: rows.map((row) => row.fileName),
      });
      for (const row of rows) {
        const result = (response.items || []).find(
          (item) => item.name === row.fileName || item.file_name === row.fileName
        );
        if (!result?.ok) {
          updateFile(row.localId, {
            status: FILE_STATUS.failed,
            error: result?.error || 'Import failed.',
          });
          ok = false;
          continue;
        }
        updateFile(row.localId, {
          status: FILE_STATUS.complete,
          fileId: result.file_id,
          contentSha256: result.content_sha256 || null,
          error: '',
        });
      }
    }
    return ok;
  }

  async function uploadOneFile(pkgId, localId) {
    const item = filesRef.current.find((file) => file.localId === localId);
    if (!item || !item.file || item.status === FILE_STATUS.complete) return;

    try {
      let fileId = item.fileId;
      let uploadUrl = item.uploadUrl;
      updateFile(localId, { status: FILE_STATUS.uploading, error: '' });

      if (!fileId) {
        const session = await createCustodianUploadSession(pkgId, {
          fileName: item.fileName,
          mimeType: item.mimeType,
          sizeBytes: item.sizeBytes,
        });
        fileId = session.id;
        uploadUrl = session.upload_url;
        updateFile(localId, {
          fileId: session.id,
          uploadUrl: session.upload_url,
          status: FILE_STATUS.uploading,
          error: '',
        });
      }

      const current = filesRef.current.find((file) => file.localId === localId);
      await uploadCustodianFileContent(pkgId, fileId, current.file, {
        uploadUrl,
        contentType: current.mimeType || undefined,
        // Large files go up in chunks; show how far along the row is.
        onProgress: ({ uploadedBytes, totalBytes }) =>
          updateFile(localId, {
            progress: totalBytes ? Math.min(100, Math.round((uploadedBytes / totalBytes) * 100)) : null,
          }),
      });

      updateFile(localId, { status: FILE_STATUS.confirming, error: '', progress: null });
      const confirmed = await confirmCustodianUpload(pkgId, fileId, {});
      if (confirmed.upload_status !== 'COMPLETE') {
        updateFile(localId, {
          status: FILE_STATUS.failed,
          error: 'Upload was not confirmed as complete.',
        });
        return;
      }
      updateFile(localId, {
        status: FILE_STATUS.complete,
        contentSha256: confirmed.content_sha256 || null,
        error: '',
      });
    } catch (requestError) {
      updateFile(localId, {
        status: FILE_STATUS.failed,
        error: requestError.message || 'Upload failed.',
      });
    }
  }

  async function handleRetryFile(localId) {
    if (isSubmitting) return;
    const item = filesRef.current.find((file) => file.localId === localId);
    if (!item || item.status !== FILE_STATUS.failed) return;
    const pkgId = packageIdRef.current;
    if (!pkgId) {
      updateFile(localId, { status: FILE_STATUS.pending, error: '' });
      setError('');
      return;
    }
    setIsSubmitting(true);
    setError('');
    setSubmitPhase('Uploading…');
    try {
      await uploadOneFile(pkgId, localId);
      const failed = filesRef.current.filter((file) => file.status === FILE_STATUS.failed);
      if (failed.length) {
        setError(
          `${failed.length} file(s) failed to upload. Successful files were kept. Retry the failed files to finish the batch.`
        );
      }
    } finally {
      setIsSubmitting(false);
      setSubmitPhase('');
    }
  }

  async function handleSubmitBatch() {
    if (!programId) {
      setError('Select a program before uploading.');
      return;
    }
    if (!profileReady) {
      setError(programmeProfileUnavailableMessage(programLookup));
      return;
    }
    if (!filesRef.current.length) {
      setError('Select at least one file before uploading.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      let pkgId = packageIdRef.current;
      if (!pkgId) {
        setSubmitPhase('Creating batch…');
        const created = await createCustodianUploadPackage({
          programId: programId.trim(),
          referenceNumber: referenceNumber.trim() || null,
        });
        pkgId = created.id;
        packageIdRef.current = pkgId;
        setPackageId(pkgId);
        setCreatedPackage(created);
      }

      if (filesRef.current.some((file) => file.source === 'inbox')) {
        setSubmitPhase('Importing from originator inbox…');
        const inboxOk = await importQueuedInboxFiles(pkgId);
        if (!inboxOk) {
          setError(
            'One or more inbox files could not be imported. Fix or remove the failed rows before continuing.'
          );
          return;
        }
      }

      setSubmitPhase('Uploading…');
      for (const item of filesRef.current) {
        if (item.status === FILE_STATUS.complete) continue;
        if (item.source === 'inbox') continue;
        await uploadOneFile(pkgId, item.localId);
      }

      const incomplete = filesRef.current.filter((file) => file.status !== FILE_STATUS.complete);
      if (incomplete.length) {
        setError(
          `${incomplete.length} file(s) failed to upload. Successful files were kept. Retry the failed files to finish the batch.`
        );
        return;
      }

      if (tapeFile) {
        setSubmitPhase('Attaching loan tape…');
        const tapeOk = await uploadQueuedTape(pkgId);
        if (!tapeOk) {
          setError(
            'The loan tape could not be attached. Files were kept; fix or remove the tape and upload again.'
          );
          return;
        }
      }

      // Same pipeline as the originator's New Submission: classify, run the
      // admission checks and certify when they pass with no blocking exception.
      setSubmitPhase('Running admission checks…');
      const outcome = await completeCustodianUploadBatch(pkgId);
      setCompletion(outcome);
      setCompleted(true);
    } catch (requestError) {
      if (requestError.code === 'ADM-001') {
        setError(programmeProfileUnavailableMessage({ admission_blocked_code: 'ADM-001' }));
      } else {
        setError(requestError.message || 'Failed to upload the batch.');
      }
    } finally {
      setIsSubmitting(false);
      setSubmitPhase('');
    }
  }

  function resetForAnotherBatch() {
    setStep(1);
    setReferenceNumber('');
    setQueuedFiles([]);
    filesRef.current = [];
    setTapeFile(null);
    setTapeStatus(TAPE_STATUS.none);
    setTapeInfo(null);
    setTapeError('');
    setPackageId(null);
    packageIdRef.current = null;
    setCreatedPackage(null);
    setCompleted(false);
    setCompletion(null);
    setError('');
  }

  const hasFailedFiles = queuedFiles.some((file) => file.status === FILE_STATUS.failed);
  const submitLabel = isSubmitting
    ? submitPhase || 'Uploading…'
    : hasFailedFiles
      ? 'Retry failed files'
      : packageId
        ? 'Continue upload'
        : 'Upload batch';

  const originatorName =
    createdPackage?.originator_organization_name ||
    programLookup?.originator_organization_name ||
    'the originator';

  if (completed) {
    const certified = Boolean(completion?.certified);
    const blocking = Number(completion?.blocking_count || 0);
    const warnings = Number(completion?.warning_count || 0);
    const panelTone = certified
      ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
      : 'border-amber-200 bg-amber-50 text-amber-900';
    const hashTone = certified ? 'text-emerald-800' : 'text-amber-800';
    return (
      <div className="space-y-6">
        <Stepper step={2} />
        <section
          aria-label={certified ? 'Batch certified' : 'Batch needs originator review'}
          className={`mx-auto max-w-4xl rounded-2xl border p-8 text-sm ${panelTone}`}
        >
          <h2 className="text-base font-semibold">
            {certified ? 'Batch certified' : 'Batch uploaded — exceptions need the originator'}
          </h2>
          <p className="mt-2">
            {queuedFiles.length} file(s)
            {tapeStatus === TAPE_STATUS.attached ? ' and the loan tape' : ''} were uploaded and
            verified for <strong>{programLookup?.name || 'the selected program'}</strong>. The batch
            is owned by <strong>{originatorName}</strong>.
          </p>
          {certified ? (
            <p className="mt-2 text-[13px]">
              Admission checks passed with no blocking exception
              {warnings ? ` (${warnings} warning${warnings === 1 ? '' : 's'})` : ''}. The package is{' '}
              <strong>Certified</strong> on the originator&apos;s behalf
              {completion?.certification_version ? ` (certification v${completion.certification_version})` : ''}{' '}
              and has been handed to the Provider for review. Nothing has been admitted to the vault
              yet.
            </p>
          ) : (
            <p className="mt-2 text-[13px]">
              The package is <strong>Classified</strong> but admission checks found{' '}
              <strong>
                {blocking} blocking exception{blocking === 1 ? '' : 's'}
              </strong>
              {warnings ? ` and ${warnings} warning${warnings === 1 ? '' : 's'}` : ''}. It was not
              certified; {originatorName} must cure the exceptions and certify it from Packages.
              Nothing has been admitted to the vault.
            </p>
          )}
          {certified && completion?.accepted_statements?.length ? (
            <details className="mt-2 text-[12px]">
              <summary className="cursor-pointer font-semibold">
                Statements accepted on the originator&apos;s behalf (
                {completion.accepted_statements.length})
              </summary>
              <ul className="mt-1 list-disc space-y-0.5 pl-5">
                {completion.accepted_statements.map((statement) => (
                  <li key={statement}>{statement}</li>
                ))}
              </ul>
            </details>
          ) : null}
          {completion?.abdp_classify_queued ? (
            <p className="mt-2 text-[13px]">
              Process classify started in the background; extract and validation continue
              automatically.
            </p>
          ) : null}
          {createdPackage?.reference_number ? (
            <p className="mt-2 text-[13px]">
              Reference number: <strong>{createdPackage.reference_number}</strong>
            </p>
          ) : null}
          <ul className="mt-4 space-y-1 text-[13px]">
            {queuedFiles.map((file) => (
              <li key={file.localId} className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{file.fileName}</span>
                {file.contentSha256 ? (
                  <span className={`truncate font-mono text-[11px] ${hashTone}`}>
                    sha256 {file.contentSha256}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button type="button" className={nextButtonClass} onClick={resetForAnotherBatch}>
              Upload another batch
            </button>
            <button
              type="button"
              className={backButtonClass}
              onClick={() => navigate('/custodian')}
            >
              Back to dashboard
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Stepper step={step} />

      {error ? (
        <div className="mx-auto max-w-4xl rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <div
        className={`mx-auto rounded-2xl border border-[#F1F1F1] bg-white p-8 shadow-[6px_6px_54px_rgba(0,0,0,0.03)] sm:p-10 ${
          step === 2 ? 'max-w-4xl' : 'max-w-xl'
        }`}
      >
        {step === 1 ? (
          <div className="space-y-6">
            <div>
              <label htmlFor="custodian-upload-program" className={labelClass}>
                Program
              </label>
              <select
                id="custodian-upload-program"
                className={`mt-1.5 ${inputClass}`}
                value={programId}
                disabled={programsLoading || programs.length === 0}
                onChange={(event) => handleSelectProgram(event.target.value)}
              >
                <option value="">
                  {programsLoading ? 'Loading programs…' : 'Select a program'}
                </option>
                {programs.map((program) => (
                  <option key={program.id} value={program.id}>
                    {program.name}
                    {program.code ? ` (${program.code})` : ''}
                    {program.originator_organization_name
                      ? ` — ${program.originator_organization_name}`
                      : ''}
                  </option>
                ))}
              </select>
              {programLookup ? (
                <p className="mt-2.5 text-xs font-semibold leading-4 text-[#003A70]">
                  Uploading for {programLookup.originator_organization_name || 'the originator'} on{' '}
                  {programLookup.name}
                  {programLookup.code ? ` (${programLookup.code})` : ''}. The batch will be owned
                  by that originator.
                </p>
              ) : !programsLoading && programs.length === 0 ? (
                <p className="mt-1.5 text-[12px] text-eorig-muted">
                  No program is assigned to this custodian organisation.
                </p>
              ) : null}
            </div>

            <div>
              <label htmlFor="custodian-upload-reference" className={labelClass}>
                Reference number
              </label>
              <input
                id="custodian-upload-reference"
                className={`mt-1.5 ${inputClass}`}
                placeholder="e.g. Monthly tape — B-10479"
                value={referenceNumber}
                onChange={(event) => setReferenceNumber(event.target.value)}
              />
            </div>

            {admissionProfile ? <AdmissionProfileCard profile={admissionProfile} /> : null}

            <p className="text-xs leading-4 text-[#5A5A5A]">
              On upload the batch is classified and the programme&apos;s admission checks run. If
              they pass with no blocking exception it is certified on the originator&apos;s behalf
              and goes to the Provider; otherwise it stays with the originator to cure. Authority
              approval and vault admission happen later in Awaiting Review.
            </p>

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                className={backButtonClass}
                onClick={() => navigate('/custodian')}
              >
                Back
              </button>
              <button
                type="button"
                className={nextButtonClass}
                onClick={() => setStep(2)}
                disabled={!programId || programsLoading || !profileReady}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-eorig-ink">Upload files</h2>
              <p className="mt-1 text-[12px] text-eorig-muted">
                Upload from this browser or import from the{' '}
                <strong>{programLookup?.originator_organization_name || 'originator'}</strong> SFTP /
                file-share inbox (same paths shown in Provider Admin for that organisation).
              </p>
            </div>

            {ingestChannels.length ? (
              <UploadChannelTabs
                channels={ingestChannels}
                activeChannel={uploadChannelTab}
                onChange={setUploadChannelTab}
                disabled={isSubmitting || ingestChannelsLoading}
              />
            ) : null}

            {uploadChannelTab === 'DEVICE' ? (
              <div
                className="rounded-xl border-2 border-dashed border-eorig-border bg-eorig-surface px-6 py-10 text-center"
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDrop={handleDrop}
              >
                <p className="text-sm font-medium text-eorig-ink">Drop PDF or ZIP files or browse</p>
                {packing ? (
                  <p className="mt-2 text-[12px] text-eorig-muted">Packing PDFs into one batch ZIP…</p>
                ) : null}
                <label className="mt-4 inline-flex cursor-pointer items-center justify-center rounded-full border border-eorig-border bg-white px-4 py-2 text-sm font-semibold text-eorig-ink transition-colors hover:bg-eorig-surface">
                  Choose files
                  <input
                    type="file"
                    multiple
                    accept={DOCUMENT_ACCEPT}
                    className="sr-only"
                    onChange={handleQueueFiles}
                  />
                </label>
              </div>
            ) : null}

            {uploadChannelTab === 'SFTP' || uploadChannelTab === 'FILE_SHARE' ? (
              <InboxFilePicker
                channel={uploadChannelTab}
                listFiles={listInboxForProgram}
                onAddFiles={handleAddInboxFiles}
                disabled={isSubmitting || !programId}
                selectedNames={new Set(queuedFiles.map((file) => file.fileName))}
              />
            ) : null}

            {queuedFiles.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-[13px] text-eorig-ink">
                  <caption className="sr-only">Queued files</caption>
                  <thead className="text-[11px] uppercase tracking-wide text-eorig-muted">
                    <tr>
                      <th className="px-2 py-2 font-semibold">File</th>
                      <th className="px-2 py-2 font-semibold">Upload status</th>
                      <th className="px-2 py-2 font-semibold">Verified SHA-256</th>
                    </tr>
                  </thead>
                  <tbody>
                    {queuedFiles.map((file) => (
                      <tr key={file.localId} className="border-t border-eorig-border">
                        <td className="px-2 py-2">
                          <span className="block max-w-[12rem] truncate">{file.fileName}</span>
                          <span className="text-[11px] text-eorig-muted">
                            {file.memberNames
                              ? `${file.memberNames.length} PDFs · ${file.sizeBytes || 0} bytes`
                              : `${file.sizeBytes || 0} bytes`}
                          </span>
                          {file.memberNames ? (
                            <span className="mt-0.5 block max-w-[16rem] truncate text-[11px] text-eorig-muted">
                              {file.memberNames.join(', ')}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-2 py-2">
                          <span
                            className={
                              file.status === FILE_STATUS.complete
                                ? 'font-semibold text-emerald-600'
                                : file.status === FILE_STATUS.failed
                                  ? 'font-semibold text-rose-600'
                                  : 'text-eorig-muted'
                            }
                          >
                            {fileStatusLabel(file.status, file.progress)}
                          </span>
                          {file.status === FILE_STATUS.failed ? (
                            <button
                              type="button"
                              onClick={() => handleRetryFile(file.localId)}
                              disabled={isSubmitting}
                              className="ml-2 font-semibold text-eorig-navy-600 hover:text-eorig-cyan disabled:opacity-40"
                            >
                              Retry
                            </button>
                          ) : null}
                        </td>
                        <td className="px-2 py-2 font-mono text-[11px] text-eorig-muted">
                          {file.contentSha256 ? (
                            <span className="block max-w-[14rem] truncate">{file.contentSha256}</span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-[13px] text-eorig-muted">No files queued yet.</p>
            )}

            <section
              aria-label="Loan tape"
              className="rounded-xl border border-eorig-border bg-white px-4 py-3"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-eorig-ink">Loan tape (optional)</h3>
                  <p className="mt-1 text-[12px] text-eorig-muted">
                    One CSV or XLSX for this batch. It is stored with the files and used during
                    Digitize validation.
                  </p>
                </div>
                <label
                  className={`inline-flex cursor-pointer items-center justify-center rounded-full border border-eorig-border bg-white px-4 py-2 text-sm font-semibold text-eorig-ink transition-colors hover:bg-eorig-surface ${
                    isSubmitting ? 'pointer-events-none opacity-50' : ''
                  }`}
                >
                  {tapeFile ? 'Replace tape' : 'Choose tape'}
                  <input
                    type="file"
                    accept={LOAN_TAPE_ACCEPT}
                    className="sr-only"
                    onChange={handleQueueTape}
                    disabled={isSubmitting}
                    data-testid="loan-tape-input"
                  />
                </label>
              </div>
              {tapeFile ? (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[13px]">
                  <div className="min-w-0">
                    <span className="block truncate font-medium text-eorig-ink">{tapeFile.name}</span>
                    <span className="text-[11px] text-eorig-muted">
                      {tapeFile.size || 0} bytes
                      {tapeInfo
                        ? ` · ${tapeInfo.row_count} row(s) · ${(tapeInfo.headers || []).length} column(s)`
                        : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={
                        tapeStatus === TAPE_STATUS.attached
                          ? 'font-semibold text-emerald-600'
                          : tapeStatus === TAPE_STATUS.failed
                            ? 'font-semibold text-rose-600'
                            : 'text-eorig-muted'
                      }
                    >
                      {tapeStatus === TAPE_STATUS.attached
                        ? 'Attached'
                        : tapeStatus === TAPE_STATUS.uploading
                          ? 'Attaching…'
                          : tapeStatus === TAPE_STATUS.failed
                            ? 'Failed'
                            : 'Queued'}
                    </span>
                    {tapeStatus !== TAPE_STATUS.attached ? (
                      <button
                        type="button"
                        onClick={handleRemoveQueuedTape}
                        disabled={isSubmitting}
                        className="font-semibold text-eorig-navy-600 hover:text-eorig-cyan disabled:opacity-40"
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {tapeError ? (
                <p className="mt-2 text-[12px] text-rose-600" role="alert">
                  {tapeError}
                </p>
              ) : null}
            </section>

            {queuedFiles.some((file) => file.status === FILE_STATUS.failed && file.error) ? (
              <ul className="space-y-1 text-[12px] text-rose-600">
                {queuedFiles
                  .filter((file) => file.status === FILE_STATUS.failed && file.error)
                  .map((file) => (
                    <li key={`${file.localId}-error`}>
                      {file.fileName}: {file.error}
                    </li>
                  ))}
              </ul>
            ) : null}

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                className={backButtonClass}
                onClick={() => setStep(1)}
                disabled={isSubmitting || Boolean(packageId)}
              >
                Back
              </button>
              <button
                type="button"
                className={nextButtonClass}
                onClick={handleSubmitBatch}
                disabled={isSubmitting || packing || queuedFiles.length === 0}
              >
                {submitLabel}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
